import { type CardDispute, CardDisputeResponseAction } from '@column/api';

import { getDisputeStageLabel } from '~/lib/disputes/labels';

import { DISPUTE_TOPOLOGIES, type DisputeStage, isLifecycleWorkflow } from './topology';

export const lifecycleStageLabel = (stage: string | undefined | null): string => {
  if (!stage) return 'Stage unavailable';
  if (stage === 'dispute_won') return 'Resolved in cardholder favor';
  return getDisputeStageLabel(stage);
};

export const getStageEvents = (dispute: CardDispute, stage: DisputeStage) =>
  (dispute.events ?? [])
    .filter((event) => event.stage === stage)
    .sort((a, b) => {
      const first = Date.parse(a.created_at);
      const second = Date.parse(b.created_at);
      // Preserve response order when a timestamp is missing; never synthesize a time.
      return Number.isFinite(first) && Number.isFinite(second) ? first - second : 0;
    });

export const getUnlinkedEvidence = (dispute: CardDispute) => {
  const eventIds = new Set((dispute.events ?? []).map((event) => event.id));
  return (dispute.evidence ?? []).filter(
    (evidence) => !evidence.card_dispute_event_id || !eventIds.has(evidence.card_dispute_event_id)
  );
};

export const getStageState = (dispute: CardDispute, stage: DisputeStage) => {
  if (stage === dispute.stage) return 'Current stage';
  if (getStageEvents(dispute, stage).length) return 'Recorded stage';
  if (!isLifecycleWorkflow(dispute.workflow)) return 'Unobserved stage';

  const topology = DISPUTE_TOPOLOGIES[dispute.workflow];
  const remaining: DisputeStage[] = [dispute.stage];
  const seen = new Set<DisputeStage>();
  while (remaining.length) {
    const next = remaining.shift()!;
    if (seen.has(next)) continue;
    seen.add(next);
    const targets = Object.keys(topology[next] ?? {}) as DisputeStage[];
    if (targets.includes(stage)) return 'Possible stage';
    remaining.push(...targets);
  }
  return 'Alternate branch';
};

export const getLifecycleActorSummary = (dispute: CardDispute): string => {
  if (dispute.stage === 'dispute_won' || dispute.stage === 'dispute_close') return 'No further response expected.';
  if (!isLifecycleWorkflow(dispute.workflow)) return 'Next actor unavailable for this workflow.';
  if (dispute.submission_status === 'pending_submission')
    return 'Column is processing queued work with the Card Network.';
  if (dispute.stage === 'arbitration') {
    return dispute.workflow === 'collaboration'
      ? 'Column operations handles issuer filing requests; the Card Network provides any network ruling. Filing is not confirmed by this stage.'
      : 'The acquirer decides whether to file; the Card Network provides any network ruling. Filing is not confirmed by this stage.';
  }
  if (dispute.stage === 'initiated')
    return 'Customer / issuer prepares the dispute; the Card Network must accept submission.';
  if (dispute.stage === 'merchant_response_recalled') return 'Next actor unavailable after the recalled response.';
  if (dispute.stage === 'disputed') return 'Waiting on the acquirer / merchant or the Card Network.';
  if (dispute.stage === 'prearbitration_responded' && dispute.workflow === 'allocation') {
    return 'Waiting on the acquirer / merchant. The issuer cannot request arbitration at this stage.';
  }
  if (
    (dispute.stage === 'merchant_responded' && dispute.workflow === 'collaboration') ||
    (dispute.stage === 'prearbitration_responded' && dispute.workflow === 'collaboration') ||
    (dispute.stage === 'prearbitration' && dispute.workflow === 'allocation')
  ) {
    return 'Customer / issuer reviews the response. Available responses depend on the current network record.';
  }
  if (dispute.stage === 'prearbitration') return 'Waiting on the acquirer / merchant to answer pre-arbitration.';
  return 'Next actor unavailable for this stage.';
};

export const getLifecycleResponseActions = (dispute: CardDispute) => {
  if (!isLifecycleWorkflow(dispute.workflow)) return [];
  if (!['requires_action', 'failed'].includes(dispute.submission_status)) return [];
  const labels: Partial<Record<CardDisputeResponseAction, string>> = {};
  if (dispute.workflow === 'collaboration' && dispute.stage === 'merchant_responded') {
    labels.accept = 'Accept merchant response';
    labels.escalate = 'Continue to pre-arbitration';
  } else if (dispute.workflow === 'collaboration' && dispute.stage === 'prearbitration_responded') {
    labels.accept = 'Accept acquirer answer';
    labels.escalate = 'Request arbitration through Column operations';
  } else if (dispute.workflow === 'allocation' && dispute.stage === 'prearbitration') {
    labels.accept = 'Accept in full';
    labels.accept_partial = 'Accept partially';
    labels.decline = 'Decline acquirer claim';
  }
  return [...new Set(dispute.allowed_response_actions ?? [])].flatMap((action) =>
    labels[action] ? [{ action, label: labels[action]! }] : []
  );
};
